import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronsUpDown, X } from 'lucide-react';
import { CONFIG, type ClientRole } from '@hero/core';
import { PartyRole } from '@hero/schema';
import {
  BuildingIcon,
  TruckIcon,
  UserIcon,
  BrokerIcon,
  CheckIcon,
  type IconProps,
} from '@hero/web-icons';
import { cn } from '@hero/web-lib';
import { Button } from '../../../ui/Button';
import { Popover, PopoverContent, PopoverTrigger } from '../../../ui/Popover';

/**
 * CRM-public role IDs in their canonical display order. Sourced from
 * the single source of truth in `@hero/core` config so backend, web,
 * and mobile all agree on the list.
 */
const CRM_PUBLIC_ROLE_IDS = CONFIG.client.CRM_PUBLIC_ROLES.map(r => r.id);

/**
 * @deprecated Read `CONFIG.client.CRM_PUBLIC_ROLES` from `@hero/core`
 * instead. Re-exported for backward compatibility with existing
 * consumers (e.g. legacy form code mapping `roles[0] -> customerType`).
 */
export const CLIENT_SELECTABLE_ROLES = CRM_PUBLIC_ROLE_IDS;

export type ClientSelectableRole = ClientRole;

const ROLE_ICON: Partial<Record<ClientRole, React.ComponentType<IconProps>>> = {
  business_customer: BuildingIcon,
  carrier_company: TruckIcon,
  broker: BrokerIcon,
  individual_customer: UserIcon,
};

const FALLBACK_ROLE_ICON: React.ComponentType<IconProps> = UserIcon;

const getRoleIcon = (role: ClientRole) => ROLE_ICON[role] ?? FALLBACK_ROLE_ICON;

export interface ClientRoleSelectorProps {
  /** Currently selected roles. */
  value: PartyRole[];
  /** Called whenever the selection changes. */
  onChange: (next: PartyRole[]) => void;
  /** Disable interaction (e.g. while submitting). */
  disabled?: boolean;
  /** Hide the helper text under the title. */
  hideHelper?: boolean;
  /** Override the trigger label. Defaults to the m-client form title. */
  label?: string;
  /** Override the trigger placeholder. */
  placeholder?: string;
  /** Inline error message rendered below the trigger. */
  error?: string;
  /** Whether to render the popover inside a Radix Portal (default: true). */
  disablePortal?: boolean;
}

/**
 * Compact multi-select dropdown for `Client.roles: PartyRole[]`.
 *
 * Mirrors the {@link SelectAuto} icon + label + description row layout
 * inside a Popover so the selector takes one line in the form instead
 * of a 2x2 card grid. Multiple roles can be selected — Hero is a
 * network, so a single Client may be carrier + broker + customer at
 * the same time.
 *
 * Available roles and their exclusivity rules come from
 * `CONFIG.client` (`@hero/core`); see `client.config.ts`.
 *
 * Replaces the legacy binary `CustomerTypeSelector`.
 */
export function ClientRoleSelector({
  value,
  onChange,
  disabled = false,
  hideHelper = false,
  label,
  placeholder,
  error,
  disablePortal = false,
}: ClientRoleSelectorProps) {
  const { t } = useTranslation('m-client');
  const [isOpen, setIsOpen] = useState(false);

  const selected = useMemo(() => new Set<PartyRole>(value), [value]);

  const toggle = (role: ClientRole) => {
    const next = new Set(selected);
    if (next.has(role)) {
      next.delete(role);
    } else {
      next.add(role);
    }
    // Apply the canonical exclusivity rules from `@hero/core` as a
    // defensive guard — the UI also pre-disables conflicting rows via
    // `CONFIG.client.isRoleDisabled` so this should be a no-op in
    // normal flows.
    const resolved = CONFIG.client.resolveRoleSelection([...next] as ClientRole[], role);
    onChange(resolved);
  };

  const clear = () => onChange([]);

  const triggerLabel = label ?? t('form.roles_field.title');
  const triggerPlaceholder =
    placeholder ?? t('form.roles_field.placeholder', { defaultValue: 'Pick one or more roles' });

  const visibleRoles = value.filter((r): r is ClientRole =>
    (CRM_PUBLIC_ROLE_IDS as readonly PartyRole[]).includes(r),
  );

  return (
    <div className="space-y-1">
      <label className="block text-sm font-medium text-gray-700 text-start">{triggerLabel}</label>

      <div className="flex items-stretch gap-1">
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              role="combobox"
              aria-expanded={isOpen}
              disabled={disabled}
              className={cn(
                'flex-1 h-10 px-3 justify-between gap-1.5 text-start font-normal bg-white',
                value.length === 0 && 'text-muted-foreground',
                error && 'border-destructive focus:border-destructive',
              )}>
              <span className="flex items-center gap-1.5 min-w-0">
                {visibleRoles.length === 0 ? (
                  <span className="truncate">{triggerPlaceholder}</span>
                ) : (
                  <SelectedRoleChips roles={visibleRoles} max={3} />
                )}
              </span>
              <ChevronsUpDown className="size-4 opacity-50 flex-shrink-0" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            disablePortal={disablePortal}
            className="p-0 w-[--radix-popover-trigger-width] min-w-[320px]">
            <div className="max-h-[320px] overflow-y-auto py-1">
              {CONFIG.client.CRM_PUBLIC_ROLES.map(({ id: role }) => {
                const Icon = getRoleIcon(role);
                const isChecked = selected.has(role);
                const isRowDisabled = CONFIG.client.isRoleDisabled(
                  role,
                  value as ClientRole[],
                );
                return (
                  <button
                    key={role}
                    type="button"
                    role="option"
                    aria-selected={isChecked}
                    aria-disabled={isRowDisabled}
                    disabled={isRowDisabled}
                    onClick={() => toggle(role)}
                    className={cn(
                      'flex w-full items-start gap-3 px-3 py-2.5 text-start',
                      isRowDisabled
                        ? 'cursor-not-allowed opacity-50'
                        : 'hover:bg-gray-50 cursor-pointer',
                      isChecked && !isRowDisabled && 'bg-primary/5',
                    )}>
                    {/*
                      Visual-only checkbox indicator. We can't use the
                      shared Radix `Checkbox` here because it renders a
                      `<button>` and we'd end up with a button-in-button
                      hydration error inside this row trigger.
                    */}
                    <span
                      aria-hidden
                      className={cn(
                        'mt-0.5 flex size-4 flex-shrink-0 items-center justify-center rounded-sm border',
                        isChecked
                          ? 'border-primary bg-primary text-white'
                          : 'border-gray-300 bg-white',
                      )}>
                      {isChecked && <CheckIcon className="size-3" />}
                    </span>
                    <Icon
                      className={cn(
                        'size-5 flex-shrink-0 mt-0.5',
                        isChecked ? 'text-primary' : 'text-gray-500',
                      )}
                    />
                    <div className="flex flex-col min-w-0 flex-1">
                      <span
                        className={cn(
                          'text-sm font-semibold leading-5 truncate',
                          isChecked ? 'text-primary' : 'text-gray-800',
                        )}>
                        {t(`roles.${role}.title`)}
                      </span>
                      <span className="text-xs font-normal leading-4 text-gray-500 mt-0.5 line-clamp-2">
                        {t(`roles.${role}.description`)}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </PopoverContent>
        </Popover>

        {value.length > 0 && !disabled && (
          <Button
            type="button"
            variant="outline"
            className="h-10 px-2"
            onClick={clear}
            aria-label={t('form.roles_field.clear', { defaultValue: 'Clear roles' })}>
            <X className="size-4" />
          </Button>
        )}
      </div>

      {error && <p className="mt-1 text-sm text-destructive">{error}</p>}
      {!error && !hideHelper && (
        <p className="text-xs text-gray-500 text-start">{t('form.roles_field.helper_text')}</p>
      )}
    </div>
  );
}

interface SelectedRoleChipsProps {
  roles: ClientRole[];
  max?: number;
}

function SelectedRoleChips({ roles, max = 3 }: SelectedRoleChipsProps) {
  const { t } = useTranslation('m-client');
  const visible = roles.slice(0, max);
  const remaining = roles.length - visible.length;

  return (
    <span className="flex items-center gap-1 min-w-0">
      {visible.map(role => {
        const Icon = getRoleIcon(role);
        return (
          <span
            key={role}
            className="inline-flex min-w-0 items-center gap-1 rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-700">
            <Icon className="size-3 flex-shrink-0 text-gray-600" />
            <span className="truncate">{t(`roles.${role}.title`)}</span>
          </span>
        );
      })}
      {remaining > 0 && (
        <span className="inline-flex flex-shrink-0 items-center rounded bg-primary/10 px-1.5 py-0.5 text-xs font-medium text-primary">
          +{remaining}
        </span>
      )}
    </span>
  );
}
